import { useQrCode } from '@/hooks/useQrCode';
import { cn } from '@/utils/format';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { FileExtension, Options } from 'qr-code-styling';
import { useState } from 'react';
import { Button } from '../ui/elements/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/elements/dropdown-menu';

type QrTheme = 'dark' | 'light';

type QrCodeProps = {
  url: string;
  useFireball?: boolean;
  options?: Options;
  className?: string;
  size?: number;
};

type FileExtensionOptions = {
  value: FileExtension;
  label: string;
};

const FileExtOptions = [
  {
    value: 'png',
    label: 'PNG - Recommended',
  },
  {
    value: 'jpeg',
    label: 'JPEG',
  },
  {
    value: 'svg',
    label: 'SVG',
  },
  {
    value: 'webp',
    label: 'WEBP',
  },
] as FileExtensionOptions[];

const BACKGROUND_COLORS: Record<QrTheme, string> = {
  dark: '#000000',
  light: '#ffffff',
};

const LIGHT_OVERRIDES: Partial<Options> = {
  dotsOptions: {
    color: '#000000',
  },
  cornersSquareOptions: {
    color: '#000000',
    type: 'square',
  },
  cornersDotOptions: {
    color: '#000000',
    type: 'square',
  },
};

export const QrCode = ({
  url,
  options = {},
  useFireball = true,
  size = 400,
  className = '',
}: QrCodeProps) => {
  const [theme, setTheme] = useState<QrTheme>('dark');

  const _options: Options = {
    ...options,
    ...(theme === 'light' ? LIGHT_OVERRIDES : {}),
    data: url,
    width: size,
    height: size,
    margin: size * 0.025,
    image: undefined,
    backgroundOptions: {
      ...options.backgroundOptions,
      color: BACKGROUND_COLORS[theme],
    },
  };

  const _printOptions = {
    ..._options,
    width: 1200,
    height: 1200,
    margin: 50,
  };

  const { ref: displayRef } = useQrCode(_options);
  const { qrCode: printableQrCode, ref: printableRef } = useQrCode(_printOptions);

  const onDownloadClick = (extension: FileExtension) => {
    if (!printableQrCode) return;
    printableQrCode.download({
      extension,
    });
  };

  return (
    <>
      <div
        className={cn(
          'flex flex-col mx-auto text-center border border-zinc-600 dark:border-zinc-800 rounded p-1',
          'max-w-[400px] max-h-[400px] overflow-hidden',
          className,
        )}
      >
        <div className="relative">
          <div className={cn('absolute z-1 top-2/3 left-1/2', `left-[calc(50%-61px)]`)}></div>
          <div ref={displayRef} />
        </div>
        <div className="flex justify-center gap-1 mt-1">
          <Button
            type="button"
            size="sm"
            variant={theme === 'dark' ? 'default' : 'outline'}
            onClick={() => setTheme('dark')}
          >
            Dark
          </Button>
          <Button
            type="button"
            size="sm"
            variant={theme === 'light' ? 'default' : 'outline'}
            onClick={() => setTheme('light')}
          >
            Light
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="icon" size="sm">
                <ArrowDownTrayIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {FileExtOptions.map((option) => (
                <DropdownMenuItem
                  key={`select-${option.value}`}
                  onClick={() => onDownloadClick(option.value)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="hidden" ref={printableRef} />
    </>
  );
};
